import React, { useEffect, useRef, useState } from 'react'
import imagesConf from 'src/images/config'
import { useEventPromoView } from 'src/hooks/usePromoView'
import Slider from 'react-slick'
import sliderSettings from 'src/configs/slider-carousel-banner'
import {
  ArrowLeftIcon,
  ArrowRightIcon,
} from 'src/images/svg/components/ArrowIcons'
import { formatAltItems } from 'src/utils/formatAltItems'
import { Skeleton } from '@acctglobal/skeleton'
import { isValidSizes } from 'src/utils/isValidSizes'

import { formatAlt } from '../../../utils/formatAlt'
import CustomDots from './CustomDots'
import DynamicImage from './DynamicImage'

import './fullwidthCarousel.scss'

export interface Item {
  sources: Sources[]
  alt: string
  href: string
  promotion: boolean
  campaignEco?: string
  promotionId?: string
  promotionCategory?: string
  promotionName?: string
  promotionPosition?: string
}

interface ImagesFullwidthCarouselProps {
  allItems: Item[]
  alt: string
  autoplay: boolean
  autoplaySpeed: number
  setAltForEvent: React.Dispatch<React.SetStateAction<string>>
  onPromoView: (promotionId: string) => void
  promoViewEventTriggered: Set<string>
  backgroundbutton: string
  textcolorbutton: string
  desktopSizes: {
    width: number
    height: number
  }
  mobileSizes: {
    width: number
    height: number
  }
}

const ImagesFullwidthCarousel = ({
  allItems,
  alt,
  autoplay,
  autoplaySpeed,
  backgroundbutton,
  textcolorbutton,
  setAltForEvent,
  onPromoView,
  promoViewEventTriggered,
  desktopSizes,
  mobileSizes,
}: ImagesFullwidthCarouselProps) => {
  const [isMobile, setIsMobile] = useState<boolean>(true)
  const [isDuplicate, setDuplicate] = useState<string[]>([])
  const sliderRef = useRef<Slider>(null)
  const [activeIndex, setActiveIndex] = useState(0)

  const heightRatio = isMobile ? 1.0773276699 : 0.4
  const height = window.innerWidth * heightRatio

  const desktopImagesSizes = isValidSizes(
    desktopSizes?.width,
    desktopSizes?.height,
    null
  )

  const mobileImagesSizes = isValidSizes(
    mobileSizes?.width,
    mobileSizes?.height,
    null
  )

  const handleSlideChange = (currentSlide: number) => {
    setActiveIndex(currentSlide)
  }

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsMobile(window?.innerWidth < 660)
      window.addEventListener('resize', () =>
        setIsMobile(window?.innerWidth < 660)
      )
    }
  }, [])

  const formattedAltAllItems = formatAltItems(allItems) as Array<
    Item & { altData: string }
  >

  const { creativeFormat, idFormat, nameFormat, positionFormat } =
    formatAlt(alt)

  const { sendPromoView } = useEventPromoView([
    {
      id: idFormat,
      name: nameFormat,
      creative: creativeFormat,
      position: positionFormat,
    },
  ])

  useEffect(() => {
    if (alt !== '' && isDuplicate.length < allItems.length) {
      sendPromoView()
      setDuplicate((state) => [...state, alt])
    }
  }, [alt])

  useEffect(() => {
    sendPromoView()
  }, [activeIndex, sendPromoView])

  const dynamicImages = formattedAltAllItems?.map((item, index: number) => {
    const [desktopSource] = item?.sources?.filter(
      (e) => e.media === '(min-width: 40em)'
    )

    const [mobileSource] = item?.sources?.filter(
      (e) => e.media !== '(min-width: 40em)'
    )

    const desktop = {
      baseUrl: desktopSource?.srcSet,
      ...imagesConf['fullwidthCarousel.desktop'],
      aspectRatio: 2.5,
      ...(desktopImagesSizes && {
        sourceWidth: desktopImagesSizes?.width,
        sourceHeight: desktopImagesSizes?.height,
      }),
    }

    const mobile = {
      baseUrl: mobileSource?.srcSet,
      ...imagesConf['fullwidthCarousel.mobile'],
      ...(mobileImagesSizes && {
        sourceWidth: mobileImagesSizes?.width,
        sourceHeight: mobileImagesSizes?.height,
      }),
    }

    const imgOptions = {
      loading: 'eager' as const,
      layout: 'constrained' as const,
      sourceHeight: 768,
      onLoad: () => {
        const isFirst = index === 0

        // Handle the first image loading logic (adjusting visibility)
        if (isFirst) {
          const fullBannerContainer = document.querySelector(
            '.itemCarousel'
          ) as HTMLElement

          const skeletonElement = document.querySelector(
            '.skeleton-container'
          ) as HTMLDivElement

          if (fullBannerContainer && skeletonElement) {
            fullBannerContainer.style.display = 'block'
            skeletonElement.style.display = 'none'
          }
        }
      },
    }

    return (
      <DynamicImage
        key={index}
        index={index}
        item={item}
        isMobile={isMobile}
        imgOptions={imgOptions}
        mobile={mobile}
        desktop={desktop}
        setAltForEvent={setAltForEvent}
        onPromoView={onPromoView}
        promoViewEventTriggered={promoViewEventTriggered}
      />
    )
  })

  return (
    <div>
      <Skeleton height={height} backgroundColor="#F4F4F4" animation />
      {dynamicImages?.length > 0 && (
        <Slider
          ref={sliderRef}
          {...sliderSettings}
          autoplay={autoplay}
          autoplaySpeed={autoplaySpeed}
          speed={700}
          dots
          cssEase="ease-out"
          afterChange={handleSlideChange}
          appendDots={() => (
            <CustomDots
              activeIndex={activeIndex}
              onClick={(index: number) => sliderRef?.current?.slickGoTo(index)}
              length={dynamicImages.length}
              autoplaySpeed={autoplaySpeed}
              autoplay={autoplay}
            />
          )}
        >
          {dynamicImages}
        </Slider>
      )}
      <button
        aria-label="arrow-left"
        className="arrow-left"
        onClick={() => sliderRef?.current?.slickPrev()}
        style={{ backgroundColor: backgroundbutton }}
      >
        <ArrowLeftIcon
          color={textcolorbutton}
          size={{ width: 15, height: 15 }}
        />
      </button>
      <button
        aria-label="arrow-right"
        className="arrow-right"
        onClick={() => sliderRef?.current?.slickNext()}
        style={{ backgroundColor: backgroundbutton }}
      >
        <ArrowRightIcon
          color={textcolorbutton}
          size={{ width: 15, height: 15 }}
        />
      </button>
    </div>
  )
}

export default ImagesFullwidthCarousel
